import { supabase } from '@/lib/supabase';
import type {
  LandingPage,
  LandingPageSignup,
  SubmitSignupPayload,
  SubmitSignupResult,
} from '@/types';

export const landingPagesApi = {
  // ── Admin: landing page CRUD ──────────────────────────────────────────────

  list: async (): Promise<LandingPage[]> => {
    const { data, error } = await supabase
      .from('landing_pages')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data as LandingPage[];
  },

  get: async (id: string): Promise<LandingPage> => {
    const { data, error } = await supabase
      .from('landing_pages')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as LandingPage;
  },

  /** Public: fetch by slug (anon, RLS filters active-only) */
  getBySlug: async (slug: string): Promise<LandingPage> => {
    const { data, error } = await supabase
      .from('landing_pages')
      .select('*')
      .eq('slug', slug)
      .single();
    if (error) throw error;
    return data as LandingPage;
  },

  create: async (payload: Partial<LandingPage>): Promise<LandingPage> => {
    const { data, error } = await supabase
      .from('landing_pages')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data as LandingPage;
  },

  update: async (id: string, payload: Partial<LandingPage>): Promise<LandingPage> => {
    const { data, error } = await supabase
      .from('landing_pages')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as LandingPage;
  },

  archive: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('landing_pages')
      .update({ status: 'closed', updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  // ── Admin: signup management ──────────────────────────────────────────────

  listSignups: async (
    landingPageId: string,
    params: { status?: string } = {}
  ): Promise<LandingPageSignup[]> => {
    let query = supabase
      .from('landing_page_signups')
      .select('*')
      .eq('landing_page_id', landingPageId)
      .order('created_at', { ascending: false });
    if (params.status) query = query.eq('status', params.status);
    const { data, error } = await query;
    if (error) throw error;
    return data as LandingPageSignup[];
  },

  approveSignup: async (signupId: string): Promise<void> => {
    const { error } = await supabase.functions.invoke('landing-approve', {
      body: { signup_id: signupId },
    });
    if (error) throw error;
  },

  rejectSignup: async (signupId: string, reason?: string): Promise<void> => {
    const { error } = await supabase
      .from('landing_page_signups')
      .update({ status: 'rejected', rejection_reason: reason ?? null })
      .eq('id', signupId);
    if (error) throw error;
  },

  // ── Public: submit signup (anon) ──────────────────────────────────────────

  /** Called by the public form — delegates to Edge function for atomic cap check */
  submitSignup: async (payload: SubmitSignupPayload): Promise<SubmitSignupResult> => {
    const { data, error } = await supabase.functions.invoke('landing-signup', {
      body: payload,
    });
    if (error) throw error;
    return data as SubmitSignupResult;
  },
};
